import { computed, onMounted, onUnmounted, ref } from 'vue'

import isSSR from '@/_base/isSSR'
import { buildInfo } from '@/config/buildInfo'

/** Arquivo emitido pelo build com o id do deploy publicado (scripts/version-file-plugin.js). */
export const VERSION_FILE_URL = '/version.json'
/** Cadência do polling com a aba visível. */
export const VERSION_CHECK_INTERVAL_MS = 5 * 60_000
/** Gatilhos por evento (aba volta a ficar visível, troca de rota) respeitam este intervalo mínimo. */
export const VERSION_CHECK_THROTTLE_MS = 60_000
/** "Depois" esconde o aviso por este tempo. */
export const UPDATE_DISMISS_MS = 30 * 60_000
/** Janela em que uma 2ª falha de chunk não recarrega de novo (evita loop de reload). */
export const CHUNK_RELOAD_GUARD_MS = 10_000
export const CHUNK_RELOAD_KEY = '@ME:chunk_reload_at'
/** Recarga automática do Modo TV: no máximo 1x nesta janela (se não resolver, fica o aviso). */
export const AUTO_RELOAD_GUARD_MS = 5 * 60_000
export const AUTO_RELOAD_KEY = '@ME:auto_reload_at'

// Estado singleton em module scope: router, error handler, App e aviso compartilham a mesma instância.
const updateAvailable = ref(false)
const dismissed = ref(false)
let dismissedUntil = 0
let lastCheckAt = 0
let inFlight: Promise<void> | null = null

const showUpdateNotice = computed(() => updateAvailable.value && !dismissed.value)

function isEnabled(): boolean {
  return !isSSR && Boolean(buildInfo.buildId)
}

/** Lê o buildId publicado. Qualquer falha (offline, 404, HTML do fallback, JSON inválido) vira `null`. */
async function fetchPublishedBuildId(): Promise<string | null> {
  try {
    const response = await fetch(`${VERSION_FILE_URL}?t=${Date.now()}`, { cache: 'no-store' })
    if (!response.ok) return null
    if (!(response.headers.get('content-type') ?? '').includes('json')) return null

    const data: unknown = await response.json()
    const buildId = (data as { buildId?: unknown } | null)?.buildId
    return typeof buildId === 'string' && buildId ? buildId : null
  } catch {
    return null
  }
}

function markUpdateAvailable(): void {
  updateAvailable.value = true
}

/** Busca o version.json e marca o aviso se o deploy publicado for outro. Nunca lança. */
function checkForUpdate(): Promise<void> {
  if (!isEnabled() || updateAvailable.value) return Promise.resolve()
  if (inFlight) return inFlight

  lastCheckAt = Date.now()
  inFlight = fetchPublishedBuildId()
    .then((publishedBuildId) => {
      if (publishedBuildId && publishedBuildId !== buildInfo.buildId) markUpdateAvailable()
    })
    .finally(() => {
      inFlight = null
    })
  return inFlight
}

/** Reexibe o aviso adiado quando o "Depois" expira. */
function resurfaceIfDismissExpired(): void {
  if (dismissed.value && Date.now() >= dismissedUntil) dismissed.value = false
}

/** Gatilho por evento: checa no máximo 1x por VERSION_CHECK_THROTTLE_MS. */
function checkIfStale(): Promise<void> {
  resurfaceIfDismissExpired()
  if (Date.now() - lastCheckAt < VERSION_CHECK_THROTTLE_MS) return Promise.resolve()
  return checkForUpdate()
}

function dismiss(): void {
  dismissed.value = true
  dismissedUntil = Date.now() + UPDATE_DISMISS_MS
}

function reloadApp(): void {
  window.location.reload()
}

/**
 * Trava de recarga automática: true (e registra agora) se nenhuma recarga com a mesma
 * chave aconteceu dentro de `windowMs`. Evita loop quando a recarga não resolve
 * (index.html antigo em cache, chunk que falha sempre).
 */
function claimAutoReload(key: string, windowMs: number): boolean {
  try {
    const lastReloadAt = Number(sessionStorage.getItem(key)) || 0
    if (Date.now() - lastReloadAt < windowMs) return false
    sessionStorage.setItem(key, String(Date.now()))
  } catch {
    // sessionStorage indisponível (modo privado antigo): segue sem a trava.
  }
  return true
}

/**
 * Falha de chunk só é "versão nova" se o version.json confirmar; senão foi a rede
 * (offline, 3G instável) e quem chamou trata como erro comum.
 */
async function confirmUpdateAvailable(): Promise<boolean> {
  await checkForUpdate()
  return updateAvailable.value
}

/**
 * Navegação falhou porque o chunk da rota não existe mais (deploy novo): carrega a
 * página de destino do zero. O usuário já estava saindo da tela (os leave guards
 * passaram), então não há dado não salvo em jogo. Se uma recarga acabou de
 * acontecer e falhou de novo, não insiste — o aviso fica visível.
 */
async function recoverFromChunkLoadError(targetPath?: string): Promise<void> {
  if (isSSR || !(await confirmUpdateAvailable())) return
  if (!claimAutoReload(CHUNK_RELOAD_KEY, CHUNK_RELOAD_GUARD_MS)) return

  if (targetPath) window.location.assign(targetPath)
  else reloadApp()
}

/** Tela sem operador (Modo TV): recarrega sozinha, no máximo 1x por AUTO_RELOAD_GUARD_MS. */
function autoReloadForUpdate(): void {
  if (claimAutoReload(AUTO_RELOAD_KEY, AUTO_RELOAD_GUARD_MS)) reloadApp()
}

/** Somente para testes: devolve o singleton ao estado inicial. */
export function resetAppVersionCheckState(): void {
  updateAvailable.value = false
  dismissed.value = false
  dismissedUntil = 0
  lastCheckAt = 0
  inFlight = null
}

export function useAppVersionCheck() {
  return {
    updateAvailable,
    showUpdateNotice,
    checkForUpdate,
    checkIfStale,
    markUpdateAvailable,
    dismiss,
    reloadApp,
    confirmUpdateAvailable,
    recoverFromChunkLoadError,
    autoReloadForUpdate
  }
}

/**
 * Liga o polling (só com a aba visível) e a checagem quando a aba volta ao primeiro
 * plano — o caso do celular que ficou dias com a página aberta. Montar UMA vez (App.vue).
 */
export function useAppVersionPolling(): void {
  let timer: ReturnType<typeof setInterval> | null = null

  function tick(): void {
    if (document.visibilityState !== 'visible') return
    resurfaceIfDismissExpired()
    void checkForUpdate()
  }

  const handleVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') void checkIfStale()
  }

  onMounted(() => {
    if (!isEnabled()) return
    timer = setInterval(tick, VERSION_CHECK_INTERVAL_MS)
    document.addEventListener('visibilitychange', handleVisibilityChange)
  })

  onUnmounted(() => {
    if (timer) clearInterval(timer)
    timer = null
    document.removeEventListener('visibilitychange', handleVisibilityChange)
  })
}
